export const profileData = {
  name: "Sai Pavan Tej Bobba",
  title: "Software Engineer",
  tagline: "AI/ML Systems • Backend Development • Cloud Infrastructure",
  subtitle: "Building RAG & Agent Solutions",
  about: "I'm a software and AI developer who prioritizes user experience in every function I build. I rigorously test my code before deploying to cloud platforms, ensuring reliability and performance. As technology evolves, I maintain strong fundamentals while staying flexible and adapting to new project requirements.",
  conversationIntro: [
    { type: "greeting", text: "Hey there! 👋", delay: 0 },
    { type: "message", text: "I'm Sai Pavan Tej Bobba", delay: 800 },
    { type: "message", text: "A Software Engineer who builds intelligent systems", delay: 1600 },
    { type: "highlight", text: "AI/ML • Backend • Cloud", delay: 2400 },
    { type: "cta", text: "Scroll down to get to know me better...", delay: 3200 }
  ],
  contact: {
    email: "pavanbobba09@gmail.com",
    phone: "404-493-5721",
    github: "https://github.com/pavanbobba09",
    linkedIn: "https://linkedin.com/in/pavanbobba09"
  }
};

export const skillsData = {
  categories: [
    {
      id: "ai-ml",
      title: "AI & Machine Learning",
      icon: "Brain",
      color: "#f4b8c5",
      skills: [
        { name: "Large Language Models", level: 95, tools: "OpenAI, Anthropic, Gemini, Ollama" },
        { name: "RAG Architecture", level: 90, tools: "Retrieval-Augmented Generation" },
        { name: "Vector Databases", level: 88, tools: "Pinecone, FAISS, pgvector" },
        { name: "AI Agents & Tool Calling", level: 92, tools: "LangChain, MCP Protocol" },
        { name: "Prompt Engineering", level: 94, tools: "LLM Evaluation & Optimization" }
      ]
    },
    {
      id: "cloud",
      title: "Cloud & DevOps",
      icon: "Cloud",
      color: "#e8c4c4",
      skills: [
        { name: "AWS Services", level: 92, tools: "Lambda, EC2, S3, DynamoDB, API Gateway" },
        { name: "GCP (Google Cloud)", level: 85, tools: "Compute Engine, Cloud Functions, BigQuery, GCS" },
        { name: "Terraform", level: 88, tools: "IaC, Modules, State Management" },
        { name: "Serverless Architecture", level: 90, tools: "Event-Driven Design" },
        { name: "Containers", level: 85, tools: "Docker, Kubernetes" },
        { name: "CI/CD", level: 87, tools: "GitHub Actions, Jenkins" }
      ]
    },
    {
      id: "backend",
      title: "Backend & Full-Stack",
      icon: "Code",
      color: "#ffa07a",
      skills: [
        { name: "Python", level: 95, tools: "FastAPI, Flask" },
        { name: "Java", level: 88, tools: "Spring Boot" },
        { name: "JavaScript/TypeScript", level: 90, tools: "Node.js, Express" },
        { name: "Databases", level: 92, tools: "PostgreSQL, MySQL, DynamoDB, Redis" },
        { name: "APIs", level: 94, tools: "REST, gRPC, WebSockets" }
      ]
    },
    {
      id: "observability",
      title: "Observability & Reliability",
      icon: "Activity",
      color: "#ff965a",
      skills: [
        { name: "Monitoring", level: 85, tools: "Prometheus, Grafana" },
        { name: "Logging", level: 88, tools: "ELK Stack, Splunk" },
        { name: "Alerting", level: 82, tools: "CloudWatch" },
        { name: "Performance Optimization", level: 90, tools: "Profiling, Caching" }
      ]
    }
  ]
};

export const projectsData = [
  {
    id: 1,
    title: "Personal Cloud Cost Optimizer",
    shortDesc: "Real-time cost estimates across AWS, GCP, and Azure",
    description: "Built a full-stack serverless application that provides real-time cost estimates across AWS, GCP, and Azure before deployment, solving unpredictable cloud billing for users without specialized cloud expertise.",
    features: [
      "Automated pricing pipeline using AWS Lambda",
      "Event-driven architecture with S3 storage",
      "Infrastructure as code using Terraform"
    ],
    techStack: ["React", "Node.js", "AWS Lambda", "S3", "SQL", "Terraform"],
    color: "#88a2ff",
    link: "https://github.com/pavanbobba09/Personal-Cloud-Cost-Optimizer",
    icon: "DollarSign"
  },
  {
    id: 2,
    title: "InboxOps-Agent",
    shortDesc: "AI email intelligence agent for natural language queries",
    description: "Built an AI email intelligence agent to automate monitoring across email accounts, enabling natural language queries like 'show rejections today' or 'what needs follow-up' to eliminate manual inbox sorting.",
    features: [
      "AI-powered email classification with LangChain",
      "Multi-account Gmail API integration",
      "Conversational query interface"
    ],
    techStack: ["Python", "LangChain", "OpenAI API", "Gmail API", "Flask", "Streamlit"],
    color: "#f4b8c5",
    link: "https://github.com/pavanbobba09/InboxOps-Agent",
    icon: "Mail"
  },
  {
    id: 3,
    title: "Interactive LLM Finance Tutor",
    shortDesc: "RAG-powered AI tutoring with sub-2-second responses",
    description: "Developed an AI-powered finance tutoring system using Retrieval-Augmented Generation (RAG) to provide accurate, context-aware responses with minimal hallucinations.",
    features: [
      "RAG pipeline with optimized semantic chunking",
      "Sub-2-second response times",
      "65% query response time reduction"
    ],
    techStack: ["Python", "LangChain", "OpenAI API", "Pinecone", "PostgreSQL"],
    color: "#f5d89a",
    link: "https://github.com/pavanbobba09/interactive-llm-tutor",
    icon: "GraduationCap"
  }
];

export const experienceData = [
  {
    id: 1,
    role: "Software Development Engineer",
    company: "Georgia State University",
    type: "Part-time",
    duration: "September 2024 - April 2025",
    location: "Atlanta, Georgia, United States",
    color: "#f4b8c5",
    description: "Building innovative software solutions for academic research and university operations."
  },
  {
    id: 2,
    role: "Software Engineer II",
    company: "DBS Bank",
    type: "Full-time",
    duration: "September 2023 - July 2024",
    location: "Hyderabad, Telangana, India",
    color: "#e6a87c",
    description: "Led backend development initiatives and mentored junior engineers in building scalable banking solutions."
  },
  {
    id: 3,
    role: "Software Engineer",
    company: "DBS Bank",
    type: "Full-time",
    duration: "July 2022 - September 2023",
    location: "Hyderabad, Telangana, India",
    color: "#d4a5a5",
    description: "Developed and maintained critical banking applications with focus on reliability and performance."
  }
];

export const conversationMessages = {
  about: [
    { speaker: "visitor", text: "Tell me about yourself?" },
    { speaker: "sai", text: "Great question! I'm a software engineer who's passionate about building intelligent systems." },
    { speaker: "sai", text: "I specialize in AI/ML, particularly RAG systems and LLM-powered applications." },
    { speaker: "sai", text: "What sets me apart? I obsess over user experience and rigorously test everything before deployment." }
  ],
  skills: [
    { speaker: "visitor", text: "What technologies do you work with?" },
    { speaker: "sai", text: "I work across the full stack, but my sweet spot is where AI meets backend engineering." }
  ],
  projects: [
    { speaker: "visitor", text: "Show me what you've built!" },
    { speaker: "sai", text: "Here are some projects I'm proud of. Hover over them to learn more!" }
  ],
  experience: [
    { speaker: "visitor", text: "Where have you worked?" },
    { speaker: "sai", text: "I've had some amazing opportunities. Let me walk you through my journey..." }
  ],
  contact: [
    { speaker: "visitor", text: "How can I reach you?" },
    { speaker: "sai", text: "I'd love to connect! Here's how you can reach me..." }
  ]
};